import React from "react";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { useSelector } from "src/store";

import { selectCreateRolloutDetails } from "@dashboard/devices/store";
import { Paper, TableContainer, TableRow } from "@mui/material";
import { Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { DateCell } from "@shared/table/DateCell";
import { MajorVersionHeader } from "@shared/table/headers/MajorVersionHeader";
import { OtaChannelHeader } from "@shared/table/headers/OtaChannelHeader";
import { OtaStatusHeader } from "@shared/table/headers/OtaStatusHeader";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { CustomTableHeader } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useGetOtaFilesQuery } from "../../api";
import { OtaFile } from "../../types";

import { VersionTableActionsCell } from "./VersionTableActionsCell";
import { VersionTableTagsCell } from "./VersionTableTagsCell";

const columns: ColumnDef<OtaFile>[] = [
    {
        header: i18n.t("common:label.version"),
        accessorKey: "version",
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue<string>()}</Typography>
            </MinimalTableCell>
        ),
    },
    {
        header: i18n.t("common:label.created"),
        accessorKey: "created",
        cell: (info) => <DateCell value={info.getValue<string | null | undefined>()} />,
    },
    {
        header: i18n.t("common:label.channel"),
        accessorKey: "channel",
        accessorFn: (row) => i18n.t(`common:label.${row.channel}`),
        cell: (info) => (
            <MinimalTableCell style={{ maxWidth: 100 }}>
                <Typography>{info.getValue<string>()}</Typography>
            </MinimalTableCell>
        ),
    },
    {
        header: i18n.t("common:label.status"),
        accessorKey: "status",
        accessorFn: (row) => i18n.t(`common:label.${row.status}`),
        cell: (info) => (
            <MinimalTableCell style={{ maxWidth: 100 }}>
                <Typography>{info.getValue<string>()}</Typography>
            </MinimalTableCell>
        ),
    },
    {
        id: "tags",
        header: i18n.t("common:label.tags"),
        enableSorting: false,
        cell: ({ row }) => <VersionTableTagsCell row={row} />,
    },
    {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => <VersionTableActionsCell row={row} />,
    },
];

type VersionTableParams = {
    pageIndex: number;
    pageSize: number;
    sortCriteria: string;
    sortDirection: "ascending" | "descending";
    includeMajorVersions: number[];
    channels: string[];
    statuses: string[];
};

const defaultParams: VersionTableParams = {
    pageIndex: 0,
    pageSize: 12,
    sortCriteria: "version",
    sortDirection: "descending",
    includeMajorVersions: [],
    channels: [],
    statuses: [],
};

type Props = {
    codename: string | null;
    workspaceId: number;
};
export const VersionTable = (props: Props) => {
    const [params, setParams] = React.useState(defaultParams);

    const { t } = useTranslation(["pages"]);

    const { statuses: selectedStatuses, ...restParams } = params;
    const statusesParam = selectedStatuses.length ? selectedStatuses : undefined;

    const { data: otaData, isFetching } = useGetOtaFilesQuery(
        props.codename
            ? { codename: props.codename, workspaceId: props.workspaceId, statuses: statusesParam, ...restParams }
            : skipToken,
    );
    const otaFiles = React.useMemo(() => otaData?.list ?? [], [otaData]);

    const details = useSelector(selectCreateRolloutDetails);

    const onSort = (id: string) => {
        setParams((prev) => ({
            ...prev,
            sortCriteria: id,
            sortDirection: prev.sortDirection === "ascending" ? "descending" : "ascending",
            pageIndex: 0,
        }));
    };

    const onMajorVersionChange = (includeMajorVersions: number[]) => {
        setParams((prev) => ({ ...prev, includeMajorVersions, pageIndex: 0 }));
    };

    const onChannelChange = (channels: string[]) => {
        setParams((prev) => ({ ...prev, channels, pageIndex: 0 }));
    };

    const onStatusChange = (statuses: string[]) => {
        setParams((prev) => ({ ...prev, statuses, pageIndex: 0 }));
    };

    const versionTable = useReactTable<OtaFile>({
        data: otaFiles,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    React.useEffect(() => {
        setParams(defaultParams);
    }, [props.codename, props.workspaceId]);

    return (
        <React.Fragment>
            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {versionTable.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <CustomTableHeader
                                        key={header.id}
                                        sortCriteria={params.sortCriteria}
                                        sortDirection={params.sortDirection === "ascending" ? "asc" : "desc"}
                                        onSort={onSort}
                                        {...header}
                                    >
                                        {header.column.id === "version" ? (
                                            <MajorVersionHeader
                                                codename={props.codename}
                                                value={params.includeMajorVersions}
                                                onChange={onMajorVersionChange}
                                            />
                                        ) : null}
                                        {header.column.id === "channel" ? (
                                            <OtaChannelHeader value={params.channels} onChange={onChannelChange} />
                                        ) : null}
                                        {header.column.id === "status" ? (
                                            <OtaStatusHeader value={params.statuses} onChange={onStatusChange} />
                                        ) : null}
                                    </CustomTableHeader>
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody<OtaFile> table={versionTable} noDataMessage={t("rolloutsPage.noVersions")} loading={isFetching}>
                        {({ rows }) =>
                            rows.map((row) => (
                                <HoverRow
                                    key={row.id}
                                    {...row}
                                    selected={details?.selectedId === row.original.buildId}
                                    clickable={false}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <React.Fragment key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </React.Fragment>
                                    ))}
                                </HoverRow>
                            ))
                        }
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <CustomTablePagination
                component="div"
                rowsPerPageOptions={[12, 24, 48]}
                count={otaData?.count ?? 0}
                rowsPerPage={params.pageSize}
                page={params.pageIndex}
                onPageChange={(_event, page) => setParams((prev) => ({ ...prev, pageIndex: page }))}
                onRowsPerPageChange={(e) => setParams((prev) => ({ ...prev, pageSize: Number(e.target.value), pageIndex: 0 }))}
            />
        </React.Fragment>
    );
};
